import { useForm } from '@tanstack/react-form';
import type { Priority } from '@voll-intelligence/domain';

import { t } from '@web/i18n';
import { Button, Textarea } from '@web/ui/core/atoms';
import { fireAndForget } from '@web/ui/core/utils';

interface CreateTaskValues {
  description: string;
  priority: Priority;
}

const DEFAULT_VALUES: CreateTaskValues = { description: '', priority: 'normal' };

/**
 * Create-task form (description + priority). Emits a validated payload to
 * `onSubmit`; no-ops on empty/whitespace description.
 */
export function CreateTaskForm({
  onSubmit,
  pending,
  onCancel,
}: Readonly<{
  onSubmit: (values: CreateTaskValues) => void;
  pending: boolean;
  onCancel: () => void;
}>): React.JSX.Element {
  const form = useForm({
    defaultValues: DEFAULT_VALUES,
    onSubmit: ({ value }) => {
      const description = value.description.trim();
      if (!description) return;
      onSubmit({ description, priority: value.priority });
    },
  });

  return (
    <form
      className="flex flex-col gap-2"
      onSubmit={(e) => {
        e.preventDefault();
        fireAndForget(form.handleSubmit());
      }}
    >
      <form.Field name="description">
        {(field) => (
          <Textarea
            aria-label={t('board.drawer.tasks.createForm.descriptionLabel')}
            placeholder={t('board.drawer.tasks.createForm.descriptionPlaceholder')}
            maxLength={2000}
            value={field.state.value}
            onChange={(e) => {
              field.handleChange(e.target.value);
            }}
          />
        )}
      </form.Field>
      <form.Field name="priority">
        {(field) => (
          <label className="flex flex-col gap-1 text-meta text-muted-foreground">
            {t('board.drawer.tasks.createForm.priorityLabel')}
            <select
              aria-label={t('board.drawer.tasks.createForm.priorityLabel')}
              className="h-8 rounded-md border border-border-soft bg-surface px-2 text-base text-foreground"
              value={field.state.value}
              onChange={(e) => {
                field.handleChange(e.target.value as Priority);
              }}
            >
              <option value="low">{t('triage.priority.low')}</option>
              <option value="normal">{t('triage.priority.normal')}</option>
              <option value="high">{t('triage.priority.high')}</option>
              <option value="urgent">{t('triage.priority.urgent')}</option>
            </select>
          </label>
        )}
      </form.Field>
      <div className="flex items-center justify-end gap-2">
        <Button type="button" variant="ghost" size="sm" onClick={onCancel}>
          {t('board.drawer.tasks.createForm.cancel')}
        </Button>
        <Button
          type="submit"
          variant="primary"
          size="sm"
          disabled={pending}
          aria-disabled={pending}
        >
          {t('board.drawer.tasks.createForm.submit')}
        </Button>
      </div>
    </form>
  );
}
